/**
 * KpiBand — banda de KPIs operativos en vivo de la Torre de control.
 * Cada tile es un dato real (operational-summary / status-count / socket).
 * El de "Nivel de servicio" lleva un aro de progreso conic-gradient.
 */
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Truck,
  PackageX,
  CheckCircle2,
  XCircle,
  Target,
  Users,
} from 'lucide-react';
import { cn } from '../../../../utils/cn';
import { Skeleton } from '../../../../components/ui/Skeleton';
import { formatNumberCL } from '../../../../utils/formatters';

const TONE = {
  brand: 'bg-brand-50 text-brand-700',
  info: 'bg-status-info-soft text-status-info',
  warning: 'bg-status-warning-soft text-status-warning',
  success: 'bg-status-success-soft text-status-success',
  error: 'bg-status-error-soft text-status-error',
  neutral: 'bg-bg-muted text-fg-muted',
};

function slColor(pct) {
  if (pct >= 90) return 'rgb(var(--status-success))';
  if (pct >= 80) return 'rgb(var(--brand-500))';
  if (pct >= 70) return 'rgb(var(--status-warning))';
  return 'rgb(var(--status-error))';
}

function Tile({ icon: Icon, tone, value, label, sub, loading }) {
  return (
    <div className="flex items-center gap-3 px-3 py-3 md:px-4 min-w-0">
      <span
        className={cn(
          'w-10 h-10 rounded-lg flex items-center justify-center shrink-0 [&>svg]:w-5 [&>svg]:h-5',
          TONE[tone] || TONE.neutral,
        )}
      >
        <Icon strokeWidth={2.1} />
      </span>
      <div className="min-w-0">
        <div className="text-[22px] font-bold tabular-nums text-fg leading-none">
          {loading ? <Skeleton className="h-6 w-12" /> : value}
        </div>
        <div className="text-[11px] uppercase tracking-wide font-semibold text-fg-muted mt-1 line-clamp-2">
          {label}
        </div>
        {sub && !loading && <div className="text-[11px] text-fg-subtle mt-0.5 line-clamp-2">{sub}</div>}
      </div>
    </div>
  );
}

function KpiBand({ kpis, activeDrivers, totalDrivers, loading }) {
  const { t } = useTranslation();
  const sl = kpis.serviceLevelPct;

  return (
    <div className="rounded-xl border border-border bg-bg shadow-card overflow-hidden">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 divide-x divide-y lg:divide-y-0 divide-border">
        <Tile
          icon={Truck}
          tone="warning"
          loading={loading}
          value={formatNumberCL(kpis.route)}
          label={t('En ruta')}
          sub={t('paquetes en reparto')}
        />
        <Tile
          icon={PackageX}
          tone="neutral"
          loading={loading}
          value={
            // Sin el conteo de entregas (null) la resta daría de más: "sin dato".
            kpis.delivered == null
              ? '—'
              : formatNumberCL(Math.max(kpis.total - kpis.delivered - kpis.failed, 0))
          }
          label={t('Pendientes')}
          sub={t('sin gestionar hoy')}
        />
        <Tile
          icon={CheckCircle2}
          tone="success"
          loading={loading}
          value={kpis.delivered == null ? '—' : formatNumberCL(kpis.delivered)}
          label={t('Entregados hoy')}
          sub={
            kpis.delivered == null
              ? undefined
              : `${formatNumberCL(kpis.delivered)} ${t('de')} ${formatNumberCL(kpis.total)}`
          }
        />
        <Tile
          icon={XCircle}
          tone="error"
          loading={loading}
          value={formatNumberCL(kpis.failed)}
          label={t('Fallidos hoy')}
          sub={t('requieren reintento')}
        />

        {/* Nivel de servicio con aro de progreso */}
        <div className="flex items-center gap-3 px-3 py-3 md:px-4 min-w-0">
          {loading ? (
            <Skeleton className="w-11 h-11 rounded-full shrink-0" />
          ) : (
            <span
              className="relative w-11 h-11 rounded-full shrink-0 grid place-items-center"
              style={{
                background: `conic-gradient(${slColor(sl)} ${Math.min(Math.max(sl, 0), 100)}%, rgb(var(--bg-subtle)) 0)`,
              }}
              aria-hidden
            >
              <span className="w-8 h-8 rounded-full bg-bg grid place-items-center">
                <Target className="w-4 h-4" style={{ color: slColor(sl) }} strokeWidth={2.2} />
              </span>
            </span>
          )}
          <div className="min-w-0">
            <div className="text-[22px] font-bold tabular-nums text-fg leading-none">
              {loading ? <Skeleton className="h-6 w-12" /> : `${Math.round(sl)}%`}
            </div>
            <div className="text-[11px] uppercase tracking-wide font-semibold text-fg-muted mt-1 line-clamp-2">
              {t('Nivel de servicio')}
            </div>
            {!loading && (
              <div className="text-[11px] text-fg-subtle mt-0.5 line-clamp-2">
                {formatNumberCL(kpis.serviceLevelBefore)} {t('a tiempo')}
              </div>
            )}
          </div>
        </div>

        <Tile
          icon={Users}
          tone="brand"
          loading={loading}
          value={`${formatNumberCL(activeDrivers)}/${formatNumberCL(totalDrivers)}`}
          label={t('Repartidores activos')}
          sub={t('con GPS en vivo')}
        />
      </div>
    </div>
  );
}

// memo: sus props solo cambian con el poll de 90s; corta los re-renders que
// bajan del padre por el tick del snapshot GPS.
export default memo(KpiBand);
